import { useState, useEffect } from "react";
import { api } from "../lib/api";

// Managed coverage for a scan: which of the hosts found from the outside are devices an
// RMM connector already knows about.
//
// Scoped to one organization on purpose. An MSP's synced estate spans every client they
// manage, and matching this client's host against another client's device would be worse
// than reporting no match — so without an organization this asks for nothing.
//
// A failure is not surfaced as an error state. Coverage is enrichment layered on top of
// a scan that already succeeded; a connector being down must never make a completed scan
// look broken. The card simply does not render.
export function useAssetCoverage(results, organizationId) {
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(false);

  // A scan run since coverage became part of the result carries its own snapshot. That is
  // the number the report was built from, so it is shown rather than recomputed — asking
  // again could quietly disagree with the stored document.
  const stored = results?.coverage;

  useEffect(() => {
    if (stored) {
      setCoverage({ summary: stored, stored: true, reason: results?.coverageMeta?.reason ?? null });
      return undefined;
    }
    if (!results || !organizationId) { setCoverage(null); return undefined; }

    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const d = await api.post("s2/api/integrations/coverage", {
          organizationId,
          // The whole result: the endpoint reads the structured `assets` blocks the
          // scanner attaches to its categories, so it stays right as more modules learn
          // to publish the hosts they discover.
          scan: { domain: results.domain, categories: results.categories },
        });
        if (!cancelled) setCoverage(d || null);
      } catch {
        if (!cancelled) setCoverage(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [results, organizationId, stored]);

  return { coverage, loading };
}
